// DEPENDENCIES
import React, { useRef } from "react";

// MUI COMPONENTS
import { TextField } from "@mui/material";

// Common typos, fixed as soon as the word is finished (a space or punctuation
// is typed after it). Keys are lowercase; the fix keeps the typed word's case.
const FIXES = {
	// Everyday typos
	teh: "the",
	hte: "the",
	adn: "and",
	nad: "and",
	taht: "that",
	thta: "that",
	tehy: "they",
	thier: "their",
	jsut: "just",
	waht: "what",
	wiht: "with",
	whit: "with",
	wich: "which",
	becuase: "because",
	beacuse: "because",
	alot: "a lot",
	untill: "until",
	recieve: "receive",
	recieved: "received",
	reciept: "receipt",
	adress: "address",
	adressed: "addressed",
	accomodate: "accommodate",
	calender: "calendar",
	comittee: "committee",
	definately: "definitely",
	occured: "occurred",
	ocurred: "occurred",
	seperate: "separate",
	seperately: "separately",
	tommorow: "tomorrow",
	tomorow: "tomorrow",
	wierd: "weird",
	beleive: "believe",
	foward: "forward",
	fowarded: "forwarded",
	follwup: "follow-up",
	folow: "follow",
	// Contractions typed without the apostrophe
	i: "I",
	im: "I'm",
	ive: "I've",
	dont: "don't",
	doesnt: "doesn't",
	didnt: "didn't",
	isnt: "isn't",
	wasnt: "wasn't",
	arent: "aren't",
	werent: "weren't",
	havent: "haven't",
	hasnt: "hasn't",
	couldnt: "couldn't",
	shouldnt: "shouldn't",
	wouldnt: "wouldn't",
	// Work words
	emial: "email",
	emailes: "emails",
	meetign: "meeting",
	meeitng: "meeting",
	scheudle: "schedule",
	shcedule: "schedule",
	reveiw: "review",
	reivew: "review",
	reveiwed: "reviewed",
	repsonse: "response",
	resposne: "response",
	porject: "project",
	proejct: "project",
	projetc: "project",
	clinet: "client",
	cleint: "client",
	buidling: "building",
	biulding: "building",
	inspeciton: "inspection",
	inpsection: "inspection",
	specifcation: "specification",
	specifcations: "specifications",
	specificaiton: "specification",
	submital: "submittal",
	submitals: "submittals",
	drawigns: "drawings",
	drwaings: "drawings",
	estiamte: "estimate",
	esitmate: "estimate",
	proposla: "proposal",
	contarctor: "contractor",
	contracter: "contractor",
	coordiantion: "coordination",
	cordination: "coordination",
	waterprofing: "waterproofing",
	roofign: "roofing",
};

// Typing one of these ends a word
const BOUNDARY = /[\s.,;:!?)]/;

// Match the typed word's case: "Teh" → "The", "TEH" → "THE"
const matchCase = (typed, fix) => {
	if (typed.length > 1 && typed === typed.toUpperCase())
		return fix.toUpperCase();
	if (typed[0] === typed[0].toUpperCase())
		return fix[0].toUpperCase() + fix.slice(1);
	return fix;
};

// A memo TextField with autocorrect: browser spellcheck underlines, plus
// common typos fixed as you type. Backspace right after a fix puts the
// typed word back, and that word is left alone for the rest of the visit.
export default function MemoField({ onChange, onKeyDown, inputProps, ...props }) {
	// The last fix, so Backspace can undo it
	const lastFix = useRef(null);
	// Words the user un-did; not fixed again
	const kept = useRef(new Set());

	const handleChange = (e) => {
		const el = e.target;
		const typed = e.nativeEvent?.data;
		lastFix.current = null;
		if (
			e.nativeEvent?.inputType === "insertText" &&
			typed?.length === 1 &&
			BOUNDARY.test(typed)
		) {
			const caret = el.selectionStart;
			const before = el.value.slice(0, caret - 1);
			// A whole word only, not the tail of "3im" or "o'dont"
			const word = before.match(/(?:^|[^\w'’-])([A-Za-z]+)$/)?.[1];
			const fix = word && FIXES[word.toLowerCase()];
			// "i" only on a space, so "i.e." is left alone
			const skip = word === "i" && typed !== " ";
			if (fix && !skip && !kept.current.has(word.toLowerCase())) {
				const fixed = matchCase(word, fix);
				if (fixed !== word) {
					const start = before.length - word.length;
					const after = before.slice(0, start) + fixed + typed;
					// Rewrite the field in place so the caret stays put; the
					// handler below then reads the fixed text
					el.value = after + el.value.slice(caret);
					el.setSelectionRange(after.length, after.length);
					lastFix.current = {
						start,
						end: after.length,
						original: word + typed,
					};
				}
			}
		}
		onChange?.(e);
	};

	const handleKeyDown = (e) => {
		const fix = lastFix.current;
		const el = e.target;
		if (
			fix &&
			e.key === "Backspace" &&
			el.selectionStart === fix.end &&
			el.selectionEnd === fix.end
		) {
			// Back to the typed word, without the space/punctuation after it
			e.preventDefault();
			const word = fix.original.slice(0, -1);
			kept.current.add(word.toLowerCase());
			const pos = fix.start + word.length;
			el.value = el.value.slice(0, fix.start) + word + el.value.slice(fix.end);
			el.setSelectionRange(pos, pos);
			lastFix.current = null;
			onChange?.({ ...e, target: el, currentTarget: el });
			return;
		}
		if (e.key !== "Shift") lastFix.current = null;
		onKeyDown?.(e);
	};

	return (
		<TextField
			{...props}
			inputProps={{
				spellCheck: true,
				autoCorrect: "on",
				autoCapitalize: "sentences",
				...inputProps,
			}}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
		/>
	);
}
